@if (isEditing) {
  <div class="card-edit-backdrop" aria-hidden="true" appScrollThrough></div>
}
<div class="contact-information-card" [class.expanded]="isExpanded" [class.editing]="isEditing">
  <!-- Action Icons (Top Right) - Edit/Expand or Cancel/Save -->
  <div class="card-actions">
    @if (!isEditing) {
      <aio-button appearance="neutral-stroke" size="xs" class="edit-button" iconSrc="assets/icons/edit.svg" ariaLabel="Edit" (pressed)="onEditClick()" />
      <button class="expand-button" (click)="onExpandClick()" [class.expanded]="isExpanded" title="Expand">
        <img src="assets/icons/chevron_down.svg" alt="Expand" />
      </button>
    } @else {
      <aio-button appearance="primary" size="sm" [block]="true" class="save-button-changes" (pressed)="onSaveClick()" [disabled]="!isSaveEnabled" title="Save Changes">
        Save Changes
      </aio-button>
      <button class="cancel-button-x" (click)="onCancelClick()" title="Cancel">
        <img src="assets/icons/x icon.svg" alt="Cancel" />
      </button>
    }
  </div>

  <!-- Header with title -->
  <div class="contact-header">
    <h3 class="contact-title">Contact Information</h3>
  </div>

  @if (!isEditing) {
    <!-- View Mode: first contact only when collapsed; all contacts when expanded -->
    @for (contact of getContacts(); track contact.participantId || $index; let i = $index; let isLast = $last) {
      @if (isExpanded || i === 0) {
        <div class="contact-item" 
             [class.expanded]="isContactExpanded(i)" 
             [class.last-visible]="(isExpanded && isLast) || (!isExpanded && i === 0)"
             (mouseenter)="toggleExpand(i)" 
             (mouseleave)="toggleExpand(i)">
        <!-- Row 1: Name and Affiliation -->
        <div class="contact-row">
          <div class="contact-field">
            <div class="contact-field-label">Name</div>
            <div class="contact-name">{{ getFullName(contact) }}</div>
          </div>
          <div class="contact-field">
            <div class="contact-field-label">Affiliation</div>
            <div class="contact-affiliation">{{ getAffiliation(contact) }}</div>
          </div>
        </div>

        <!-- Row 2: Phone Numbers (left) and Email (right) -->
        <div class="contact-row">
          <div class="contact-field">
            <div class="contact-field-label">Phone Numbers</div>
            <div class="contact-phones">
              @if (getPhoneNumbers(contact).length > 0) {
                @for (phone of getPhoneNumbers(contact); track phone.type) {
                  <div class="phone-item">
                    <span class="phone-type">{{ phone.type }}</span>
                    <span class="phone-dial-and-number">
                      <span class="phone-country-code">{{ getCountryCodePrefix(phone.countryCode) }}</span><span class="phone-number">{{ formatPhoneNumberOnly(phone.number, phone.extension) }}</span>
                    </span>
                  </div>
                }
              } @else {
                <div class="phone-item">
                  <span class="phone-number">-</span>
                </div>
              }
            </div>
          </div>
          <div class="contact-field">
            <div class="contact-field-label">Email</div>
            <div class="contact-email">
              @if (getEmail(contact) !== '-') {
                <a [href]="'mailto:' + getEmail(contact)" class="email-link">{{ getEmail(contact) }}</a>
              } @else {
                <span class="email-link" style="text-decoration: none; cursor: default;">-</span>
              }
            </div>
          </div>
        </div>
      </div>
      }
      
      <!-- Show divider between contacts only when expanded -->
      @if (!isLast && isExpanded) {
        <div class="contact-divider"></div>
      }
    }
  } @else {
    <!-- Edit Mode -->
    @for (contact of editContacts; track $index; let i = $index) {
      <div class="contact-edit-section">
        <!-- Row 1: First Name and Last Name -->
        <div class="edit-row">
          <div class="edit-field">
            <aio-input-field label="First Name" [required]="true" [fullWidth]="true" [(ngModel)]="contact.firstName" placeholder="Add first name" />
          </div>
          <div class="edit-field">
            <aio-input-field label="Last Name" [required]="true" [fullWidth]="true" [(ngModel)]="contact.lastName" placeholder="Add last name" />
          </div>
        </div>

        <!-- Primary Contact Toggle -->
        <div class="primary-contact-row">
          <label class="primary-contact-toggle">
            <input type="checkbox" [checked]="contact.isPrimary" (change)="onPrimaryContactToggle(i, $event)" class="primary-contact-toggle-input" />
            <span class="primary-contact-slider"></span>
            <span class="primary-contact-text">Primary Contact</span>
          </label>
        </div>

        <!-- Affiliation -->
        <div class="edit-row">
          <div class="edit-field">
            <aio-input-field label="Affiliation" [fullWidth]="true" [(ngModel)]="contact.affiliation" placeholder="Homeowner, family member, etc..." />
          </div>
        </div>

        <!-- Phone Numbers -->
        @if (contact.phoneNumbers && contact.phoneNumbers.length > 0) {
          @for (phone of contact.phoneNumbers; track phoneIndex; let phoneIndex = $index) {
            <div class="edit-row phone-number-row">
              <div class="edit-field phone-type-field">
                <aio-select
                  class="edit-select"
                  label="Type"
                  [(ngModel)]="phone.phoneNumberType"
                  [options]="phoneTypeOptions"
                  [fullWidth]="true"
                  placeholder="Select"
                  (ngModelChange)="onPhoneTypeChange(i, phoneIndex, $event)" />
              </div>
              <div class="edit-field phone-number-field">
                <aio-phone-field
                  label="Phone Number"
                  [countries]="phoneCountries"
                  [country]="phone.countryCode"
                  [value]="formatPhoneNumberForInput(phone.phoneNumber)"
                  [extension]="phone.phoneExtension"
                  [showExtension]="phone.phoneNumberType === 'work'"
                  (countryChange)="onPhoneCountryChange(i, phoneIndex, $event)"
                  (valueChange)="onPhoneValueChange(i, phoneIndex, $event)"
                  (extensionChange)="onPhoneExtensionChange(i, phoneIndex, $event)"
                />
              </div>
              @if (contact.phoneNumbers.length > 1) {
                <div class="edit-field remove-phone-field">
                  <aio-button appearance="neutral-stroke" size="md" type="button" class="remove-phone-btn" iconName="delete" ariaLabel="Remove phone number" (pressed)="removePhoneNumber(i, phoneIndex)" />
                </div>
              }
            </div>
          }
        }

        <!-- Add Phone Number Link (multi: align under phone input column) -->
        <div class="add-phone-link" [class.add-phone-link-multi]="(contact.phoneNumbers?.length ?? 0) > 1">
          <aio-button appearance="tertiary" size="sm" type="button" class="add-phone-btn" [class.add-phone-btn-disabled]="(contact.phoneNumbers?.length ?? 0) >= 5" [disabled]="(contact.phoneNumbers?.length ?? 0) >= 5" (pressed)="addPhoneNumber(i)">
            <svg class="add-phone-btn__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M11.9997 8.66658H8.66634V11.9999C8.66634 12.3666 8.36634 12.6666 7.99967 12.6666C7.63301 12.6666 7.33301 12.3666 7.33301 11.9999V8.66658H3.99967C3.63301 8.66658 3.33301 8.36658 3.33301 7.99992C3.33301 7.63325 3.63301 7.33325 3.99967 7.33325H7.33301V3.99992C7.33301 3.63325 7.63301 3.33325 7.99967 3.33325C8.36634 3.33325 8.66634 3.63325 8.66634 3.99992V7.33325H11.9997C12.3663 7.33325 12.6663 7.63325 12.6663 7.99992C12.6663 8.36658 12.3663 8.66658 11.9997 8.66658Z" />
            </svg>
            Add Number
          </aio-button>
        </div>

        <!-- Email -->
        <div class="edit-row-full">
          <div class="edit-field">
            <aio-input-field label="Email" type="email" [required]="true" [fullWidth]="true" [(ngModel)]="contact.email" placeholder="Add email address" />
          </div>
        </div>

        <!-- Delete Contact Button (not for first contact) -->
        @if (i > 0 || editContacts.length > 1) {
          <div class="remove-contact-row">
            <aio-button appearance="ghost" size="sm" type="button" class="remove-contact-btn" iconName="delete" (pressed)="removeContact(i)">
              Delete Contact
            </aio-button>
          </div>
        }
      </div>
    }

    <!-- Add New Contact Button -->
    <div class="add-contact-row">
      <aio-button appearance="secondary" size="sm" type="button" class="add-new-contact-btn" (pressed)="addNewContact()">
        <svg class="add-new-contact-btn__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M11.9997 8.66658H8.66634V11.9999C8.66634 12.3666 8.36634 12.6666 7.99967 12.6666C7.63301 12.6666 7.33301 12.3666 7.33301 11.9999V8.66658H3.99967C3.63301 8.66658 3.33301 8.36658 3.33301 7.99992C3.33301 7.63325 3.63301 7.33325 3.99967 7.33325H7.33301V3.99992C7.33301 3.63325 7.63301 3.33325 7.99967 3.33325C8.36634 3.33325 8.66634 3.63325 8.66634 3.99992V7.33325H11.9997C12.3663 7.33325 12.6663 7.63325 12.6663 7.99992C12.6663 8.36658 12.3663 8.66658 11.9997 8.66658Z" />
        </svg>
        Add New Contact
      </aio-button>
    </div>
  }
</div>
